import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography
} from '@mui/material';
import { Refresh } from '@mui/icons-material';
import api, { stockAPI } from '../../utils/api';
import { useApp } from '../../context/AppContext';

const Stock = () => {
  const { actions } = useApp();
  const [stores, setStores] = useState([]);
  const [products, setProducts] = useState([]);
  const [storeId, setStoreId] = useState('');
  const [stockMap, setStockMap] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [updatingSkuMap, setUpdatingSkuMap] = useState({});

  const skuRows = useMemo(() => {
    const rows = [];
    for (const product of products) {
      const variants = Array.isArray(product.variants) ? product.variants : [];
      for (const variant of variants) {
        const sku = String(variant.sku || '').trim().toUpperCase();
        if (!sku) continue;
        rows.push({
          productId: product.id,
          productName: product.name || 'Unnamed Product',
          variantLabel: variant.weight || '-',
          sku
        });
      }
    }
    return rows;
  }, [products]);

  const fetchStoresAndProducts = async () => {
    try {
      setLoading(true);
      const [storesRes, productsRes] = await Promise.all([
        api.get('/stores'),
        api.get('/products', {
          params: {
            page: 1,
            limit: 500,
            status: 'active'
          }
        })
      ]);

      const storesData = storesRes.data?.stores || [];
      const productsData = productsRes.data?.data || [];
      setStores(storesData);
      setProducts(productsData);

      if (!storeId && storesData.length > 0) {
        setStoreId(storesData[0].id);
      }
    } catch (fetchError) {
      console.error('Error loading stock dependencies:', fetchError);
      setError('Failed to load stores or products.');
    } finally {
      setLoading(false);
    }
  };

  const fetchBranchStock = async (selectedStoreId) => {
    if (!selectedStoreId) return;
    try {
      const response = await stockAPI.getBranchStock(selectedStoreId);
      setStockMap(response.data?.data || {});
    } catch (fetchError) {
      console.error('Error fetching branch stock:', fetchError);
      actions.showSnackbar('Failed to fetch branch stock', 'error');
      setStockMap({});
    }
  };

  useEffect(() => {
    fetchStoresAndProducts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchBranchStock(storeId);
  }, [storeId]);

  const handleToggle = async (sku, checked) => {
    if (!storeId) return;
    const previousValue = Boolean(stockMap[sku]);

    setStockMap((prev) => ({
      ...prev,
      [sku]: checked
    }));
    setUpdatingSkuMap((prev) => ({ ...prev, [sku]: true }));

    try {
      await stockAPI.upsertStock({
        storeId,
        sku,
        isAvailable: checked
      });
    } catch (saveError) {
      console.error('Error saving stock:', saveError);
      setStockMap((prev) => ({
        ...prev,
        [sku]: previousValue
      }));
      actions.showSnackbar(`Failed to update ${sku}`, 'error');
    } finally {
      setUpdatingSkuMap((prev) => ({ ...prev, [sku]: false }));
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Stock Management
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage stock availability by branch and SKU.
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>Store</InputLabel>
          <Select
            value={storeId}
            onChange={(event) => setStoreId(event.target.value)}
            label="Store"
          >
            {stores.map((store) => (
              <MenuItem key={store.id} value={store.id}>
                {store.storeName} ({store.branchId})
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button
          variant="outlined"
          startIcon={<Refresh />}
          onClick={() => fetchBranchStock(storeId)}
          disabled={!storeId}
        >
          Refresh Stock
        </Button>
      </Paper>

      <Paper>
        <TableContainer>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>SKU</TableCell>
                <TableCell>Product</TableCell>
                <TableCell>Variant</TableCell>
                <TableCell>In Stock</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    Loading stock data...
                  </TableCell>
                </TableRow>
              ) : skuRows.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={4} align="center">
                    No product SKUs found.
                  </TableCell>
                </TableRow>
              ) : (
                skuRows.map((row) => (
                  <TableRow key={`${row.productId}-${row.sku}`} hover>
                    <TableCell>{row.sku}</TableCell>
                    <TableCell>{row.productName}</TableCell>
                    <TableCell>{row.variantLabel}</TableCell>
                    <TableCell>
                      <Switch
                        checked={Boolean(stockMap[row.sku])}
                        onChange={(event) => handleToggle(row.sku, event.target.checked)}
                        disabled={!storeId || Boolean(updatingSkuMap[row.sku])}
                      />
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default Stock;
